import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Page } from "../components/AppShell";
import {
  Badge,
  Card,
  Pager,
  PanelState,
  StatTile,
  Swatch,
} from "../components/ui";
import { useAuth } from "../auth/AuthContext";
import { useRevoke } from "../lib/useRevoke";
import {
  date,
  dateTime,
  device,
  num,
  relative,
  titleCase,
  usd,
} from "../lib/format";
import { useApi } from "../lib/useApi";
import type { RenderColor, UserDetail } from "../lib/types";

function hexOf(color: RenderColor): string | null {
  const raw = color.hexCode ?? color.hex;
  return raw && /^#[0-9a-fA-F]{6}$/.test(raw) ? raw : null;
}

/**
 * ── The page a sales conversation is prepared from ─────────────────────────
 * Not a profile dump: a history. "Tried Winter Hat, then Deep Indigo,
 * downloaded the third one, hearted four blues" is a call worth making, and it
 * is not visible anywhere else in the business.
 */
export function CustomerDetailPage() {
  const { id = "" } = useParams();
  const { canRevoke } = useAuth();
  const [projectPage, setProjectPage] = useState(1);

  // ── The page number belongs to the customer, not to the component ────────
  // React Router keeps this component mounted when only `:id` changes, so
  // clicking from one customer to the next carries the room page across.
  // Landing on page 5 of someone who has three rooms shows an empty card under
  // a heading that says "3 rooms in total" — which reads as broken data, not
  // as a stale page number. Reset during render rather than in an effect, so
  // the request that goes out is already for page 1.
  const [pagedFor, setPagedFor] = useState(id);
  if (pagedFor !== id) {
    setPagedFor(id);
    setProjectPage(1);
  }
  const page = pagedFor === id ? projectPage : 1;

  const { data, error, loading, stale, reload } = useApi<{ user: UserDetail }>(
    id ? `/api/admin/users/${id}` : null,
    { page, pageSize: 10 },
  );
  const { revokeUser, revokeSession, busyId, toast } = useRevoke(reload);

  const user = data?.user;
  const liveSessions = user?.sessions.filter((s) => s.live) ?? [];

  return (
    <Page
      title={user?.fullName ?? "Customer"}
      subtitle={user ? `Signed up ${date(user.createdAt)}` : "Loading…"}
      actions={
        <div style={{ display: "flex", gap: 8 }}>
          <Link className="btn btn-sm" to="/customers">
            Back to list
          </Link>
          {canRevoke && user && liveSessions.length > 0 && (
            <button
              className="btn btn-sm btn-danger"
              disabled={busyId === user.id}
              onClick={() =>
                void revokeUser(
                  user.id,
                  user.fullName ?? "this customer",
                  "customer detail page",
                )
              }
            >
              {busyId === user.id
                ? "Ending…"
                : `End all ${liveSessions.length} sessions`}
            </button>
          )}
        </div>
      }
    >
      <PanelState loading={loading} error={error}>
        {user && (
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <div className="grid grid-kpi">
              <StatTile label="Rooms" value={user.counts.projects} />
              <StatTile label="Views generated" value={user.counts.renders} />
              <StatTile
                label="Views downloaded"
                value={user.counts.downloads}
                hint="took it to a paint counter"
              />
              <StatTile
                label="Shades hearted"
                value={user.counts.favorites}
              />
              {/* An account with no recorded calls shows a dash, not $0.00:
                  its views may simply predate usage tracking. */}
              <StatTile
                label="AI spend (est.)"
                value={user.ai.calls > 0 ? usd(user.ai.costUsd) : "—"}
                hint={
                  user.ai.calls > 0
                    ? `${num(user.ai.totalTokens)} tokens · ${num(user.ai.calls)} call${user.ai.calls === 1 ? "" : "s"}${user.ai.unpricedCalls > 0 ? ` · ${num(user.ai.unpricedCalls)} unpriced` : ""}`
                    : "no model calls recorded"
                }
              />
            </div>

            <Card title="Profile">
              <div className="detail-grid">
                <Detail k="Phone" v={<span className="mono">{user.phone}</span>} />
                <Detail k="Email" v={user.email ?? "—"} />
                <Detail k="Pincode" v={user.pincode ?? "—"} />
                <Detail k="Type" v={titleCase(user.profileType)} />
                <Detail
                  k="Onboarded"
                  v={
                    user.onboarded ? (
                      <Badge tone="good">Complete</Badge>
                    ) : (
                      <Badge tone="warning">Incomplete</Badge>
                    )
                  }
                />
                <Detail k="Last login" v={relative(user.lastLoginAt)} />
                <Detail k="Heard about us" v={user.reference ?? "—"} />
                <Detail
                  k="Customer id"
                  v={<span className="mono">{user.id}</span>}
                />
              </div>
            </Card>

            <Card
              title="Devices"
              note="Every signed-in device. Ending one signs out that device alone; ending all of them is the button in the header."
            >
              <PanelState
                loading={false}
                error={null}
                empty={user.sessions.length === 0}
                emptyText="Never signed in."
              >
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Device</th>
                        <th>Address</th>
                        <th>Last seen</th>
                        <th>Signed in</th>
                        <th>Status</th>
                        {canRevoke && <th />}
                      </tr>
                    </thead>
                    <tbody>
                      {user.sessions.map((s) => (
                        <tr key={s.id}>
                          <td className="cell-strong">
                            {device(s.userAgent)}
                          </td>
                          <td className="mono cell-mute">{s.ip ?? "—"}</td>
                          <td className="cell-mute">{relative(s.lastSeenAt)}</td>
                          <td className="cell-mute">{date(s.createdAt)}</td>
                          <td>
                            {s.live ? (
                              <Badge tone="good">Live</Badge>
                            ) : s.revokedAt ? (
                              <Badge>Ended {relative(s.revokedAt)}</Badge>
                            ) : (
                              <Badge>Expired</Badge>
                            )}
                          </td>
                          {canRevoke && (
                            <td style={{ textAlign: "right" }}>
                              {s.live && (
                                <button
                                  className="btn btn-sm btn-danger"
                                  disabled={busyId === s.id}
                                  onClick={() =>
                                    void revokeSession(
                                      s.id,
                                      `${device(s.userAgent)} · ${s.ip ?? "unknown address"} · last seen ${relative(s.lastSeenAt)}`,
                                    )
                                  }
                                >
                                  {busyId === s.id ? "Ending…" : "End"}
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </PanelState>
            </Card>

            <Card
              title="Hearted shades"
              note="What they liked without committing to a render."
            >
              <PanelState
                loading={false}
                error={null}
                empty={user.favorites.length === 0}
                emptyText="Nothing hearted yet."
              >
                <div
                  style={{ display: "flex", flexWrap: "wrap", gap: 10 }}
                >
                  {user.favorites.map((f) => (
                    <span
                      key={f.code}
                      className="swatch-row"
                      style={{
                        padding: "5px 10px 5px 6px",
                        border: "1px solid var(--border)",
                        borderRadius: 999,
                      }}
                      title={`${f.code} · hearted ${date(f.createdAt)}`}
                    >
                      <Swatch hex={f.hexCode} small />
                      <span style={{ fontSize: 12 }}>{f.name}</span>
                    </span>
                  ))}
                </div>
              </PanelState>
            </Card>

            <Card
              title="Rooms and views"
              note="Most recently touched first, with the shades that produced each view."
              aside={`${num(user.counts.projects)} rooms in total`}
            >
              <PanelState
                loading={false}
                error={null}
                empty={user.projects.length === 0}
                emptyText="No rooms started."
              >
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 20 }}
                >
                  {user.projects.map((p) => (
                    <div key={p.id}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: 10,
                          flexWrap: "wrap",
                          marginBottom: 9,
                        }}
                      >
                        <strong>{p.name}</strong>
                        <span className="cell-mute">
                          {titleCase(p.roomType)} · {titleCase(p.finishId)} ·{" "}
                          {num(p.renderCount)} view
                          {p.renderCount === 1 ? "" : "s"} · updated{" "}
                          {relative(p.updatedAt)}
                          {p.ai && (
                            <>
                              {" "}
                              · AI {usd(p.ai.costUsd)} ({num(p.ai.totalTokens)}{" "}
                              tokens)
                            </>
                          )}
                        </span>
                        {p.maskStatus === "FAILED" && (
                          <Badge tone="critical">Mask failed</Badge>
                        )}
                        {p.maskStatus === "PENDING" && (
                          <Badge tone="warning">Mask pending</Badge>
                        )}
                        {p.templateId && <Badge>Sample room</Badge>}
                      </div>

                      {p.renders.length === 0 ? (
                        <p className="cell-mute">No views generated yet.</p>
                      ) : (
                        <div className="render-wall">
                          {p.renders.map((r) => (
                            <figure
                              key={r.id}
                              className="render-tile"
                              style={{ margin: 0 }}
                            >
                              {/* referrerPolicy so the CDN never sees this
                                  console's URL in a Referer header. */}
                              <img
                                src={r.imageUrl}
                                alt={`View of ${p.name} generated ${date(r.createdAt)}`}
                                loading="lazy"
                                referrerPolicy="no-referrer"
                              />
                              <figcaption className="render-meta">
                                {dateTime(r.createdAt)}
                                {r.downloads > 0 && (
                                  <> · downloaded {num(r.downloads)}×</>
                                )}
                                {r.ai && (
                                  <div title="Tokens the model reported for this view, and its estimated cost">
                                    {num(r.ai.totalTokens)} tokens ·{" "}
                                    {usd(r.ai.costUsd)}
                                  </div>
                                )}
                                <span className="render-colors">
                                  {r.colors.slice(0, 8).map((c, i) => {
                                    const hex = hexOf(c);
                                    return hex ? (
                                      <span
                                        key={i}
                                        style={{ background: hex }}
                                        title={`${c.name ?? ""} ${c.code ?? hex}`}
                                      />
                                    ) : null;
                                  })}
                                </span>
                              </figcaption>
                            </figure>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <Pager
                  meta={user.projectPagination}
                  onChange={setProjectPage}
                  busy={stale}
                  label="rooms"
                />
              </PanelState>
            </Card>
          </div>
        )}
      </PanelState>
      {toast}
    </Page>
  );
}

function Detail({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="detail-item">
      <div className="k">{k}</div>
      <div className="v">{v}</div>
    </div>
  );
}
